import { FunctionComponent } from "react";

import {
	faArrowRight,
	faCheck,
	faTrash,
	faTriangleExclamation,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Toggle } from "@/components/Toggle/Toggle";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { InputField } from "@/components/InputField/InputField";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import {
	FieldMappingAiConfig,
	FieldMappingSourceType,
	MsdynamicsField,
	MsdynamicsFieldMapping,
	MsdynamicsSourceField,
} from "../schema/dynamics";
import {
	canOfferTargetField,
	isMappingDroppedBySync,
	isRelationshipTargetBlocked,
} from "./dynamicsFieldMapping.utils";
import {
	AiHintBanner,
	AiSourceCell,
	CannotMapRow,
	SourceTypePills,
	TargetFieldBadges,
} from "./fieldMapping";
import { DynamicsValueMapPanel } from "./fieldMapping/DynamicsValueMapPanel";

type Ts = (key: string, options?: Record<string, unknown>) => string;

const buildBadgeLabels = (ts: Ts) => ({
	required: ts("metaBadge.required"),
	multi: ts("metaBadge.multi"),
	dropdown: ts("metaBadge.dropdown"),
	number: ts("metaBadge.number"),
	calculated: ts("metaBadge.calculated"),
	readOnly: ts("metaBadge.readOnly"),
	relationship: ts("metaBadge.relationship"),
});

/**
 * Dataverse marks a calculated/rollup attribute invalid for create *and* update, so
 * `isReadOnly` is the load-bearing guard; `isCalculated` is kept because it is strictly
 * more informative when the environment reports it.
 */
const isFieldUnwritable = (field: MsdynamicsField | undefined): boolean =>
	Boolean(field?.isReadOnly) || Boolean(field?.isCalculated);

/**
 * Marks a stored mapping every sync skips (a plain value onto a relationship attribute, saved
 * before PREC-4599 refused it), so the customer sees on the page what the server log says.
 */
const DroppedBySyncBadge: FunctionComponent<{ ts: Ts }> = ({ ts }) => (
	<Tooltip content={ts("droppedBySyncTooltip")}>
		<span
			className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 bg-status-warning-light text-status-warning cursor-help"
			data-testid="dynamics-badge-dropped-by-sync"
		>
			<Icon className="w-3 h-3" icon={faTriangleExclamation} />
			{ts("droppedBySync")}
		</span>
	</Tooltip>
);

interface MappingRowProps {
	mapping: MsdynamicsFieldMapping;
	sourceFields: MsdynamicsSourceField[];
	msdynamicsFields: MsdynamicsField[];
	onToggleActive: (id: number, isActive: boolean) => void;
	onDelete: (id: number) => void;
	onConfigureAi: (mapping: MsdynamicsFieldMapping) => void;
	ts: Ts;
}

export const DynamicsMappingRow: FunctionComponent<MappingRowProps> = ({
	mapping,
	sourceFields,
	msdynamicsFields,
	onToggleActive,
	onDelete,
	onConfigureAi,
	ts,
}) => {
	const isConstant = mapping.sourceType === FieldMappingSourceType.CONSTANT;
	const isAi = mapping.sourceType === FieldMappingSourceType.AI;
	const msdynamicsField = msdynamicsFields.find((field) => field.name === mapping.msdynamicsField);
	const targetLabel = msdynamicsField
		? `${msdynamicsField.label}${msdynamicsField.isCustom ? " (Custom)" : ""}`
		: (mapping.msdynamicsFieldLabel ?? mapping.msdynamicsField);
	const badgeLabels = buildBadgeLabels(ts);

	if (isFieldUnwritable(msdynamicsField)) {
		return (
			<CannotMapRow
				badgeLabels={badgeLabels}
				cannotMapLabel={ts("cannotMap")}
				targetField={msdynamicsField || {}}
				targetFieldLabel={targetLabel}
				testIdPrefix="dynamics"
				tooltip={ts("cannotMapTooltip")}
				onDelete={() => onDelete(mapping.id)}
			/>
		);
	}

	const sourceLabel = isConstant
		? ts("constantLabel", { value: mapping.sourceField })
		: (sourceFields.find((field) => field.name === mapping.sourceField)?.label ??
			mapping.sourceField);

	const valueMap = mapping.valueMap;
	const droppedBySync = isMappingDroppedBySync(mapping, msdynamicsField, sourceFields);

	return (
		<div className="flex flex-col py-3 border-b border-border-light">
			<div className="flex items-center gap-3">
				<div className="flex-1">
					{isAi ? (
						<AiSourceCell
							badgeLabel={ts("aiBadge")}
							configureLabel={ts("aiConfigure")}
							creditTooltip={ts("aiCreditTooltip")}
							label={targetLabel}
							testIdPrefix="dynamics"
							onConfigure={() => onConfigureAi(mapping)}
						/>
					) : (
						<div
							className={classNames(
								"py-[9px] px-3 rounded-xl text-ssm border border-border-light bg-brand-white text-text",
								isConstant && "italic text-text-meta"
							)}
							data-testid={isConstant ? "dynamics-constant-label" : "dynamics-field-label"}
						>
							{sourceLabel}
						</div>
					)}
				</div>
				<div className="w-[24px] flex items-center justify-center">
					<Icon className="w-[16px] h-[16px] text-text-meta" icon={faArrowRight} />
				</div>
				<div className="flex-1">
					<div
						className={classNames(
							"py-[9px] px-3 rounded-xl text-ssm border bg-brand-white text-text flex items-center gap-2",
							droppedBySync ? "border-status-warning" : "border-border-light"
						)}
						data-testid="dynamics-target-label"
					>
						<span className="flex-1 truncate">{targetLabel}</span>
						{droppedBySync && <DroppedBySyncBadge ts={ts} />}
						<TargetFieldBadges
							field={msdynamicsField || {}}
							labels={badgeLabels}
							testIdPrefix="dynamics"
						/>
					</div>
				</div>
				<div className="w-[60px] flex items-center justify-center">
					<Toggle
						isChecked={mapping.isActive}
						onChange={() => onToggleActive(mapping.id, !mapping.isActive)}
					/>
				</div>
				<div
					className="w-fit bg-status-error-light flex justify-end p-1 rounded border-border-light group hover:border-text-status-error cursor-pointer"
					onClick={() => onDelete(mapping.id)}
				>
					<Icon className="text-status-error cursor-pointer w-4 h-4" icon={faTrash} />
				</div>
			</div>
			{valueMap && (
				<DynamicsValueMapPanel
					mapping={{ ...mapping, valueMap }}
					targetField={msdynamicsField}
					ts={ts}
				/>
			)}
		</div>
	);
};

export interface NewDynamicsMapping {
	tempId: string;
	sourceField: string;
	sourceType: FieldMappingSourceType;
	aiConfig?: FieldMappingAiConfig;
	msdynamicsField: string;
	isActive: boolean;
	entityType: string;
}

interface NewMappingRowProps {
	mapping: NewDynamicsMapping;
	sourceFields: MsdynamicsSourceField[];
	msdynamicsFields: MsdynamicsField[];
	usedSourceFields: string[];
	usedTargetFieldsByType: Record<FieldMappingSourceType, string[]>;
	onUpdate: (tempId: string, data: Partial<NewDynamicsMapping>) => void;
	onSave: (mapping: NewDynamicsMapping) => void;
	onCancel: (tempId: string) => void;
	onConfigureAi: (mapping: NewDynamicsMapping) => void;
	isSaving: boolean;
	ts: Ts;
}

export const DynamicsNewMappingRow: FunctionComponent<NewMappingRowProps> = ({
	mapping,
	sourceFields,
	msdynamicsFields,
	usedSourceFields,
	usedTargetFieldsByType,
	onUpdate,
	onSave,
	onCancel,
	onConfigureAi,
	isSaving,
	ts,
}) => {
	const isConstant = mapping.sourceType === FieldMappingSourceType.CONSTANT;
	const isAi = mapping.sourceType === FieldMappingSourceType.AI;
	const usedTargetFields = usedTargetFieldsByType[mapping.sourceType] ?? [];

	const availableSourceFields = sourceFields.filter(
		(field) => field.name === mapping.sourceField || !usedSourceFields.includes(field.name)
	);
	const selectedSourceField = sourceFields.find((field) => field.name === mapping.sourceField);
	const availableTargetFields = msdynamicsFields.filter(
		(field) =>
			(field.name === mapping.msdynamicsField || !usedTargetFields.includes(field.name)) &&
			!isFieldUnwritable(field) &&
			canOfferTargetField(field, selectedSourceField)
	);

	const sourceOptions: AutoCompleteItem[] = availableSourceFields.map((field) => ({
		id: field.name,
		title: field.label,
	}));

	const targetOptions: AutoCompleteItem[] = availableTargetFields.map((field) => ({
		id: field.name,
		title: `${field.label}${field.isCustom ? " (Custom)" : ""}`,
	}));

	const selectedSource = sourceOptions.find((option) => option.id === mapping.sourceField);
	const selectedTarget = targetOptions.find((option) => option.id === mapping.msdynamicsField);
	const selectedTargetField = msdynamicsFields.find(
		(field) => field.name === mapping.msdynamicsField
	);
	const relationshipTargetBlocked = isRelationshipTargetBlocked(
		selectedTargetField,
		selectedSourceField
	);
	const canSave =
		!relationshipTargetBlocked &&
		(isAi
			? Boolean(mapping.msdynamicsField && mapping.aiConfig?.prompt)
			: Boolean(mapping.sourceField) && Boolean(mapping.msdynamicsField));
	const saveDisabledTooltip = relationshipTargetBlocked
		? ts("relationshipNeedsValueMap")
		: isAi && !mapping.aiConfig?.prompt
			? ts("aiSaveDisabledTooltip")
			: "";
	const badgeLabels = buildBadgeLabels(ts);

	const handleSourceTypeChange = (next: FieldMappingSourceType) => {
		if (next === mapping.sourceType) {
			return;
		}

		onUpdate(mapping.tempId, { sourceType: next, sourceField: "", aiConfig: undefined });
	};

	return (
		<div className="flex flex-col gap-2 py-3 border-b border-border-light bg-brand-white rounded-lg px-2">
			<div className="flex items-start gap-3">
				<div className="flex-1 flex flex-col gap-2">
					<SourceTypePills
						labels={{
							fromRecord: ts("sourceTypeFromRecord"),
							constant: ts("sourceTypeConstant"),
							ai: ts("sourceTypeAi"),
						}}
						testIdPrefix="dynamics"
						value={mapping.sourceType}
						onChange={handleSourceTypeChange}
					/>
					{isAi ? (
						<AiSourceCell
							badgeLabel={ts("aiBadge")}
							configureDisabled={!mapping.msdynamicsField}
							configureLabel={ts("aiConfigure")}
							creditTooltip={ts("aiCreditTooltip")}
							label={selectedTarget?.title || ""}
							placeholder={ts("aiTargetPlaceholder")}
							testIdPrefix="dynamics"
							onConfigure={() => onConfigureAi(mapping)}
						/>
					) : isConstant ? (
						<InputField
							handleChange={(event) =>
								onUpdate(mapping.tempId, { sourceField: event.target.value })
							}
							maxLength={255}
							name={`dynamics-constant-${mapping.tempId}`}
							placeholder={ts("constantPlaceholder")}
							testId="dynamics-constant-input"
							value={mapping.sourceField}
						/>
					) : (
						<AutoComplete
							data={sourceOptions}
							handleReset={() => onUpdate(mapping.tempId, { sourceField: "" })}
							handleSearch={() => {}}
							handleSelect={(item) => {
								if (item) {
									onUpdate(mapping.tempId, { sourceField: item.id as string });
								}
							}}
							placeholder={ts("selectField")}
							selectedItem={selectedSource?.title || ""}
						/>
					)}
				</div>
				<div className="w-[24px] flex items-center justify-center pt-9">
					<Icon className="w-[16px] h-[16px] text-text-meta" icon={faArrowRight} />
				</div>
				<div className="flex-1 pt-9 flex flex-col gap-1">
					<AutoComplete
						data={targetOptions}
						handleReset={() =>
							onUpdate(mapping.tempId, {
								msdynamicsField: "",
								sourceField: isAi ? "" : mapping.sourceField,
							})
						}
						handleSearch={() => {}}
						handleSelect={(item) => {
							if (item) {
								onUpdate(mapping.tempId, {
									msdynamicsField: item.id as string,
									sourceField: isAi ? item.title : mapping.sourceField,
								});
							}
						}}
						placeholder={ts("selectField")}
						selectedItem={selectedTarget?.title || ""}
					/>
					{selectedTargetField && (
						<TargetFieldBadges
							field={selectedTargetField}
							labels={badgeLabels}
							testIdPrefix="dynamics-new"
						/>
					)}
				</div>
				<div className="w-[60px] flex items-center justify-center pt-9">
					<Toggle
						isChecked={mapping.isActive}
						onChange={() => onUpdate(mapping.tempId, { isActive: !mapping.isActive })}
					/>
				</div>
				<div className="w-[80px] flex gap-2 pt-9">
					<Tooltip content={saveDisabledTooltip} disabled={!saveDisabledTooltip}>
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION}
								disabled={!canSave}
								image={
									<Icon className="w-4 h-4 min-w-4 min-h-4 shrink-0 text-white" icon={faCheck} />
								}
								isLoading={isSaving}
								size={ButtonSize.XS}
								testId="dynamics-save-new-mapping"
								title=""
								onClick={() => onSave(mapping)}
							/>
						</div>
					</Tooltip>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="w-4 h-4 min-w-4 min-h-4 shrink-0 text-text" icon={faXmark} />}
							size={ButtonSize.XS}
							title=""
							onClick={() => onCancel(mapping.tempId)}
						/>
					</div>
				</div>
			</div>
			{isAi && (
				<AiHintBanner body={ts("aiHintBody")} testIdPrefix="dynamics" title={ts("aiHintTitle")} />
			)}
		</div>
	);
};
